import { useState } from 'react';
import { useCrudPermissions } from '@/hooks/useCrudPermissions';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ColumnDef } from '@tanstack/react-table';
import { CrudTable } from '@/components/CrudTable';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormTextField } from '@/components/form/FormTextField';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPublicHolidays,
  createPublicHoliday,
  updatePublicHoliday,
  deletePublicHoliday,
  type IPublicHoliday,
} from '@/api/leave.api';
import { getOffices } from '@/api/org.api';
import { safeArray } from '@/lib/safeArray';
import { getApiErrorMessage, buildErrorToast } from '@/lib/apiError';

const COUNTRY_OPTIONS = [
  { value: 'PH', label: '🇵🇭 Philippines' },
  { value: 'AU', label: '🇦🇺 Australia' },
  { value: 'US', label: '🇺🇸 United States' },
  { value: 'VN', label: '🇻🇳 Vietnam' },
];

const holidaySchema = z.object({
  date: z.string().min(1, 'Date is required'),
  name: z.string().min(1, 'Name is required').max(200),
  countryCode: z.string().min(2, 'Country is required').max(2),
  officeId: z.string().optional(),
  isHalfDay: z.boolean(),
  note: z.string().optional(),
});

type HolidayFormValues = z.infer<typeof holidaySchema>;

const columns: ColumnDef<IPublicHoliday>[] = [
  {
    accessorKey: 'date',
    header: 'Date',
    size: 12,
    cell: ({ row }) => new Date(row.original.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
  },
  { accessorKey: 'name', header: 'Holiday Name', size: 38 },
  {
    accessorKey: 'countryCode',
    header: 'Country',
    size: 18,
    cell: ({ row }) => {
      const c = COUNTRY_OPTIONS.find(o => o.value === row.original.countryCode);
      return c ? c.label : row.original.countryCode;
    },
  },
  {
    id: 'office',
    header: 'Office',
    size: 17,
    accessorFn: (r) => r.office?.name ?? 'All offices',
    cell: ({ row }) => row.original.office?.name ?? 'All offices',
  },
  {
    accessorKey: 'isHalfDay',
    header: '½ Day',
    size: 5,
    cell: ({ row }) => row.original.isHalfDay ? '½' : '—',
  },
];

export function PublicHolidaysPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<IPublicHoliday | null>(null);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [filterCountry, setFilterCountry] = useState('');
  const queryClient = useQueryClient();

  const { toast } = useToast();
  const errToast = buildErrorToast(toast);
  const perm = useCrudPermissions('S48');

  const { data: holidays, isLoading } = useQuery({
    queryKey: ['public-holidays', filterYear, filterCountry],
    queryFn: () => getPublicHolidays({
      year: filterYear,
      ...(filterCountry ? { countryCode: filterCountry } : {}),
    }),
  });

  const { data: officesData } = useQuery({
    queryKey: ['offices-for-holidays'],
    queryFn: () => getOffices({ limit: 100 }),
  });

  const createMut = useMutation({
    mutationFn: createPublicHoliday,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['public-holidays'] }),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Parameters<typeof updatePublicHoliday>[1] }) => updatePublicHoliday(id, dto),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['public-holidays'] }),
  });
  const deleteMut = useMutation({
    mutationFn: deletePublicHoliday,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['public-holidays'] }),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<HolidayFormValues>({
    resolver: zodResolver(holidaySchema) as import('react-hook-form').Resolver<HolidayFormValues>,
    defaultValues: { isHalfDay: false },
  });

  function handleAdd() {
    setEditTarget(null);
    reset({ isHalfDay: false, countryCode: 'PH' });
    setDialogOpen(true);
  }

  function handleEdit(row: IPublicHoliday) {
    setEditTarget(row);
    reset({
      date: row.date.split('T')[0],
      name: row.name,
      countryCode: row.countryCode,
      officeId: row.officeId ?? undefined,
      isHalfDay: row.isHalfDay,
    });
    setDialogOpen(true);
  }

  async function handleDelete(row: IPublicHoliday) {
    try {
      await deleteMut.mutateAsync(row.id);
      toast({ title: 'Holiday deleted' });
    } catch (err: unknown) {
      toast({ title: getApiErrorMessage(err, 'Failed to delete holiday'), variant: 'destructive' });
    }
  }

  async function onSubmit(values: HolidayFormValues) {
    try {
      if (editTarget) {
        await updateMut.mutateAsync({ id: editTarget.id, dto: values });
        toast({ title: 'Holiday updated' });
      } else {
        await createMut.mutateAsync(values);
        toast({ title: 'Holiday created' });
      }
      setDialogOpen(false);
    } catch (err: unknown) {
      errToast(err, editTarget ? 'Failed to update' : 'Failed to create');
    }
  }

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => (currentYear - 1 + i).toString());

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold" data-testid="page-title-public-holidays">Public Holidays</h1>
        <p className="text-sm text-muted-foreground">Manage public holidays by country and office</p>
      </div>

      <div className="flex gap-3 items-center">
        <Select value={filterYear} onValueChange={setFilterYear}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterCountry || '__ALL__'} onValueChange={(v) => setFilterCountry(v === '__ALL__' ? '' : v)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All countries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__ALL__">All countries</SelectItem>
            {COUNTRY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <CrudTable
        columns={columns}
        data={safeArray(holidays)}
        isLoading={isLoading}
        onAdd={perm.canCreate ? handleAdd : undefined}
        onEdit={perm.canUpdate ? handleEdit : undefined}
        onDelete={perm.canDelete ? handleDelete : undefined}
        addLabel="Add Holiday"
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
          </DialogHeader>
          <form id="holiday-form" onSubmit={handleSubmit(onSubmit)} className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
            <div className="space-y-1">
              <Label htmlFor="date">Date *</Label>
              <Input id="date" type="date" {...register('date')} />
              {errors.date && <p className="text-xs text-destructive">{errors.date.message}</p>}
            </div>

            <FormTextField
              id="name"
              label="Holiday Name"
              required
              registration={register('name')}
              current={watch('name')}
              maxLength={200}
              error={errors.name?.message}
            />

            <div className="space-y-1">
              <Label>Country *</Label>
              <Controller
                control={control}
                name="countryCode"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.countryCode && <p className="text-xs text-destructive">{errors.countryCode.message}</p>}
            </div>

            <div className="space-y-1">
              <Label>Office (optional — empty = all offices)</Label>
              <Controller
                control={control}
                name="officeId"
                render={({ field }) => (
                  <Select value={field.value ?? '__ALL__'} onValueChange={(v) => field.onChange(v === '__ALL__' ? undefined : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All offices" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__ALL__">All offices</SelectItem>
                      {safeArray(officesData?.data).map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="isHalfDay">Half Day</Label>
              <Switch
                id="isHalfDay"
                checked={watch('isHalfDay')}
                onCheckedChange={(v) => setValue('isHalfDay', v)}
              />
            </div>
          </form>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="submit" form="holiday-form" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
